import {useContext, useMemo, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import MoleculeCarousel from '@s-ui/react-molecule-carousel'

import {
  COUNTER_POSITIONS,
  GALLERY_ASPECT_RATIO,
  ITEMS_LIST_CLASS,
  SLIDER_BASE_CLASS,
  SLIDER_IMAGE_DESIGNS
} from '../../config.js'
import GalleryBasicCounter from '../Counter.js'
import GalleryBasicSliderVideo from './Video/index.js'
import GalleryBasicSliderImage from './Image.js'

const NO_OP = () => null

const getSlides = ({
  aspectRatio,
  currentSlide,
  displayVideoControls,
  hasSingleImage,
  images,
  onVideoClick,
  onVideoPlay,
  setIsVideoPlaying,
  sliderImageDesign,
  videoApiRef
}) => {
  const firstImage = images.find(image => image.isVideo !== true)
  return images.map((image, index) => {
    if (image.isVideo === true) {
      return (
        <GalleryBasicSliderVideo
          key={`${image.url}-${index}`}
          autoPlay={image.autoPlay}
          currentSlide={currentSlide}
          displayVideoControls={displayVideoControls}
          firstImage={firstImage}
          index={index}
          onVideoClick={onVideoClick}
          onVideoPlay={() => {
            setIsVideoPlaying(true)
            onVideoPlay()
          }}
          sliderImageDesign={sliderImageDesign}
          url={image.url}
          videoApiRef={videoApiRef}
          manualPlay={image.manualPlay}
          timeOffset={image.timeOffset}
          title={image.title}
        />
      )
    }

    if (typeof image.src === 'string') {
      return (
        <GalleryBasicSliderImage
          key={`${image.src}-${index}`}
          aspectRatio={aspectRatio}
          image={image}
          index={index}
          sliderImageDesign={sliderImageDesign}
          hasSingleImage={hasSingleImage}
          currentSlide={currentSlide}
        />
      )
    }

    return image
  })
}

const GalleryBasicSlider = ({
  aspectRatio,
  counterPosition,
  currentSlide = 0,
  displayVideoControls,
  enableCounter = false,
  handleClick = NO_OP,
  hideCounterWhenPlayingVideo = false,
  images = [],
  onVideoClick = NO_OP,
  onVideoPlay = NO_OP,
  sliderImageDesign,
  sliderOptions = {},
  videoApiRef
}) => {
  const {i18n} = useContext(Context)
  const [isVideoPlaying, _setIsVideoPlaying] = useState(false)
  const isVideoPlayingRef = useRef(isVideoPlaying)
  const setIsVideoPlaying = value => {
    isVideoPlayingRef.current = value
    _setIsVideoPlaying(value)
  }
  const [initSliderStyles, setInitSliderStyles] = useState(true)

  const target = useRef(null)

  const handleOnInitCarousel = () => {
    initSliderStyles &&
      target.current
        .getElementsByClassName(ITEMS_LIST_CLASS)[0]
        ?.setAttribute(
          'style',
          'transition-timing-function: ease;transition-duration: 500ms;transform: translate3d(0%, 0px, 0px);'
        )

    setInitSliderStyles(false)
  }

  const hasSingleImage = images.length === 1
  const slides = useMemo(
    () =>
      getSlides({
        aspectRatio,
        currentSlide,
        displayVideoControls,
        hasSingleImage,
        images,
        onVideoClick,
        onVideoPlay: data => isVideoPlayingRef.current === true && onVideoPlay(data), // Send the event once, when isVideoPlaying has been updated
        setIsVideoPlaying,
        sliderImageDesign,
        videoApiRef
      }),
    [currentSlide]
  )

  const handleOnSlideAfter = ({currentSlide}) => {
    setIsVideoPlaying(false)
    sliderOptions.onSlideAfter && sliderOptions.onSlideAfter(currentSlide)
  }
  const isWideAspectRatio = aspectRatio === GALLERY_ASPECT_RATIO.wideScreen
  const containerClassName = cx(SLIDER_BASE_CLASS, {
    [`${SLIDER_BASE_CLASS}--wide`]: isWideAspectRatio
  })

  return slides.length > 0 ? (
    <div onClick={handleClick} className={containerClassName} ref={target}>
      {slides.length > 1 ? (
        <MoleculeCarousel
          {...sliderOptions}
          arrowLeftLabel={i18n.t('COMMON.PREVIOUS')}
          arrowRightLabel={i18n.t('COMMON.NEXT')}
          onSlideAfter={handleOnSlideAfter}
          onInit={handleOnInitCarousel}
        >
          {slides}
        </MoleculeCarousel>
      ) : (
        slides
      )}
      {enableCounter && (!isVideoPlaying || !hideCounterWhenPlayingVideo) && (
        <GalleryBasicCounter counterPosition={counterPosition} currentSlide={currentSlide} images={images} />
      )}
    </div>
  ) : null
}

GalleryBasicSlider.displayName = 'GalleryBasicSlider'
GalleryBasicSlider.propTypes = {
  aspectRatio: PropTypes.string,
  counterPosition: PropTypes.oneOf(Object.values(COUNTER_POSITIONS)),
  currentSlide: PropTypes.number,
  displayVideoControls: PropTypes.bool,
  enableCounter: PropTypes.bool,
  handleClick: PropTypes.func,
  hideCounterWhenPlayingVideo: PropTypes.bool,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string,
      key: PropTypes.string
    }).isRequired
  ),
  onVideoClick: PropTypes.func,
  onVideoPlay: PropTypes.func,
  sliderImageDesign: PropTypes.oneOf(Object.values(SLIDER_IMAGE_DESIGNS)),
  /**
   * Custom configuration options to pass to molecule/carousel component.
   */
  sliderOptions: PropTypes.shape({
    defaultSlide: PropTypes.number,
    hasLazyLoadSlider: PropTypes.bool,
    imageObjectFit: PropTypes.oneOf(['cover', 'contain']),
    itemsToPreload: PropTypes.number,
    numOfSlides: PropTypes.number,
    onSlideAfter: PropTypes.func
  }),
  videoApiRef: PropTypes.object
}

export default GalleryBasicSlider
